import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyAutocompleteTrigger as MatAutocompleteTrigger } from '@angular/material/legacy-autocomplete';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MaskCnpjPipe } from './../../pipes/maskCnpj.pipe';

@Component({
  selector: 'app-autocomplete',
  standalone: false,
  styleUrls: ['./autocomplete.component.css'],
  templateUrl: './autocomplete.component.html',
})
export class AutoCompleteComponent implements OnInit, OnChanges {
  defaultText: string = 'Selecione';
  filteredOptions: Observable<string[]> = new Observable<string[]>();
  filteredMostUsedOptions: Observable<string[]> = new Observable<string[]>();
  filteredOtherOptions: Observable<string[]> = new Observable<string[]>();
  noValues: boolean;
  formControl: UntypedFormControl = new UntypedFormControl();
  @Input('showId') showId: boolean;
  @Input('border') border: boolean;
  @Input('selectedText') selectedText: string = this.defaultText;
  @Input('options') options: Array<any> = new Array<any>();
  @Input('displayValue') displayValue: string = 'id';
  @Input('displayText') displayText: string = 'text';
  @Input('showFilter') showFilter: boolean = true;
  @Input('disabled') disabled: boolean = false;
  @Input('mostUsed') mostUsed: boolean = false;
  @Input('alternateMostUsed') alternateMostUsed: boolean = false;
  @Input('resetAutocomplete') resetAutocomplete;
  @Input('isMaskCnpj') isMaskCnpj: boolean = false;
  @Input('isMaskCnpjCpf') isMaskCnpjCpf: boolean = false;
  @Input('isMaskConta') isMaskConta: boolean = false;
  @Input('isSelectedTextId') isSelectedTextId: boolean = true;
  @Input('isShowClearButton') isShowClearButton: boolean = false;
  @Input('tab') tabindex: number = 0;
  @Input() valueChanges: boolean = true;
  @Input() selectUniqueOption: boolean = false;
  @Output('selectedValue') selectedValue: EventEmitter<any> =
    new EventEmitter<any>();
  @ViewChild('autocompleteContainer', { read: ElementRef, static: true })
  private autocompleteContainer: ElementRef;
  @ViewChild('autocompleteInput', { read: ElementRef, static: false })
  private autocompleteInput: ElementRef;
  @ViewChild('autocompleteTriggerInput', { read: ElementRef, static: true })
  private autocompleteTriggerInput: ElementRef;
  @ViewChild('divAutocompleteTriggerInput', { read: ElementRef, static: true })
  private divAutocompleteTriggerInput: ElementRef;
  @ViewChild(MatAutocompleteTrigger, { static: true })
  private trigger: MatAutocompleteTrigger;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private maskCnpjPipe: MaskCnpjPipe
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options && changes.options.currentValue) {
      this.handleFormControlChanges();
    }
  }

  handleFormControlChanges(): void {
    // se flag de most used estiver habilitada
    // ordenar filteredOptions pelo maior valor (number) do campo frequencia, e atualizar filteredMostUsed
    if (this.mostUsed) {
      let sortedOptions = [];
      let topMostUsedOptions = [];
      let otherOptions = [];

      //Most used alternativo - uso de boolean com true/false para mais utilizado (combo subconta - nova op)
      if (this.alternateMostUsed) {
        // Prioritize items with mais_utilizado set to true
        sortedOptions =
          this.options?.length > 0
            ? this.options.sort((a, b) => {
                if (a.mais_utilizado === b.mais_utilizado) {
                  return b.frequencia - a.frequencia;
                }
                return a.mais_utilizado ? -1 : 1;
              })
            : this.options;

        //pega os 3 primeiros com mais_utilizado = true
        topMostUsedOptions = sortedOptions
          ?.filter((option) => option.mais_utilizado)
          .slice(0, 3);

        //restante da lista (lista ordenada retirando os topMostUsed)
        otherOptions = sortedOptions?.filter(
          (option) => !topMostUsedOptions.includes(option)
        );

        // Filter by search value if present
      const searchValue = this.formControl.value
        ? this._normalizeString(this.formControl.value)
        : '';

      if (searchValue) {
        topMostUsedOptions = topMostUsedOptions.filter((option) =>
          this._normalizeString(option[this.displayValue]).includes(searchValue) ||
          this._normalizeString(option[this.displayText]).includes(searchValue)
        );
        otherOptions = otherOptions.filter((option) =>
          this._normalizeString(option[this.displayValue]).includes(searchValue) ||
          this._normalizeString(option[this.displayText]).includes(searchValue)
        );
      }
      } else {
        // Prioritize items with mais_utilizado set to true
        sortedOptions =
          this.options?.length > 0
            ? this.options.sort((a, b) => {
                return b.frequencia - a.frequencia;
              })
            : this.options;

        if (this.formControl.value) {
          sortedOptions = this._filtrarOpcoesComAcentos();
        }

        //pega os 3 mais utilizados
        topMostUsedOptions = sortedOptions?.slice(0, 3);

        //restante da lista
        otherOptions = sortedOptions?.slice(3, sortedOptions.length);
        //se tiver input para filtro
      }

      if (this.showFilter) {
        this.filteredMostUsedOptions = of(
          this._filterWithArray(this.formControl.value, topMostUsedOptions)
        );
        this.filteredOtherOptions = of(
          this._filterWithArray(this.formControl.value, otherOptions)
        );
      }
      //caso contrario
      else {
        this.filteredMostUsedOptions = of(topMostUsedOptions);
        this.filteredOtherOptions = of(otherOptions);
      }
    }
    if (this.options?.length && this.defaultText !== this.selectedText) {
      this.selectedText = this.getText(this.selectedText);
    }

    // Seleciona a opção se o combobox houver apenas um item.
    if (this.selectUniqueOption === true && this.options.length === 1) {
      this.selectedText = this.getText(this.options[0][this.displayValue]);
    }
  }

  ngOnInit(): void {
    if (this.valueChanges === true) {
      this.formControl.valueChanges
        .pipe(
          startWith(''),
          map(() => {
            this.handleFormControlChanges();
          })
        )
        .subscribe();
    }

    //Verifica texto no input e filtra opções
    this.filteredOptions = this.formControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value))
    );

    //Reset autocomplete se houver evento de trigger na variável
    if (this.resetAutocomplete) {
      this.resetAutocomplete.subscribe(() => {
        this.reset();
      });
    }
  }

  //Reseta input de busca
  resetSearch() {
    this.formControl.setValue('');
  }

  setAutoCompleteValue(value) {
    this.selectedText = this.getText(value);
  }

  getText(value): string {
    const text =
      this.showId && this.isSelectedTextId
        ? this.displayValue
        : this.displayText;
    const option = this.options?.find((o) => o[this.displayValue] == value);

    if (option) this.emit(option[this.displayValue]);
    else {
      this.emit(null);
      return this.defaultText;
    }

    return option[text];
  }

  emit(value) {
    this.selectedValue.emit(value);
  }

  opened() {
    this.formControl.setValue('');

    if (this.showFilter) {
      setTimeout(() => {
        this.autocompleteInput.nativeElement.focus();
      });
    }
  }

  onClick(event?: any) {
    if (this.disabled) return;

    if (!this.trigger.autocomplete.isOpen)
      setTimeout(() => {
        this.trigger.openPanel();
      });
  }

  applyFormatting(text: string, setting?: string): string {
    if (this.isMaskCnpj) {
      return this.maskCnpjPipe.transform(text, false);
    } else {
      return text;
    }
  }

  @HostListener('document:click', ['$event']) onBlur(e: MouseEvent): void {
    if (!this.trigger.autocomplete.isOpen) return;

    const container = this.autocompleteContainer.nativeElement;
    const input = this.autocompleteTriggerInput.nativeElement;

    if (
      container &&
      container !== e.target &&
      !container.contains(e.target) &&
      input &&
      input !== e.target &&
      !input.contains(e.target)
    ) {
      this.trigger.closePanel();
      if (this.selectedText && this.defaultText != this.selectedText)
        this.formControl.markAsUntouched();
    }
  }

  private _filter(value: any): string[] {
    const filterValue = value?.toString().toLowerCase().replace(/[-.:]/g, ''); // Remove dashes, periods, and colons
    const options = this.options?.filter((option) =>
      (
        option[this.displayValue]
          ?.toString()
          .toLowerCase()
          .replace(/[-.:]/g, '') + // Remove dashes, periods, and colons
        ' ' +
        option[this.displayText]?.toString().toLowerCase().replace(/[-.:]/g, '')
      ) // Remove dashes, periods, and colons
        .includes(filterValue)
    );

    this.noValues = !options?.length;
    this.changeDetector.detectChanges();

    return options;
  }

  reset() {
    this.setAutoCompleteValue(null);
  }

  onKeydown(event) {
    if (event.code === 'Enter') {
      this.onClick();
    } else if (event.code === 'Space') {
      this.onClick();
    }
  }

  focus() {
    this.divAutocompleteTriggerInput.nativeElement.focus();
  }

  // método que faz a busca com e sem acentos;
  private _filtrarOpcoesComAcentos(): Array<any> {
    const inputValue = this.formControl.value?.toString().toLowerCase();
    const inputValueConvertedWithoutAccents = this._normalizeString(inputValue);

    // filtrar lista original comparando com  equivalentes sem acento
    let listaFiltradaComAcentos = this.options.filter((option) => {
      return (this._normalizeString(option[this.displayValue]).includes(
        inputValueConvertedWithoutAccents) || this._normalizeString(option[this.displayText]).includes(inputValueConvertedWithoutAccents)
      );
    });

    return listaFiltradaComAcentos;
  }

  // Remove acentos e converte a string para minúsculas para facilitar a comparação.
  private _normalizeString(value: string): string {
    if (!value) return '';
    const strValue = typeof value === 'string' ? value : String(value);
    return strValue
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
      .replace(/[^a-zA-Z0-9]/g, '')   // Remove special characters and spaces
      .toLowerCase();
  }

  //filter with an array as parameter
  private _filterWithArray(value: any, options: any[]): string[] {
    if (value) {
      const filteredOptions = options.filter(
        (option) =>
          option[this.displayValue]
            ?.toString()
            .toLowerCase()
            .replace(/[-.:]/g, '') +
          ' ' +
          option[this.displayText]
            ?.toString()
            .toLowerCase()
            .replace(/[-.:]/g, '')
      );

      this.noValues = !filteredOptions.length;
      this.changeDetector.detectChanges();

      return filteredOptions;
    } else return options;
  }
}
